import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" },
  searchInput: { flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }
};

const BLANK = { title: "", author: "", isbn: "", category: "", totalCopies: "1", description: "" };

const CATEGORIES = ["Textbook", "Reference", "Fiction", "Periodical", "Thesis", "Journal", "Other"];

class LibraryCatalogue extends Component {
  state = {
    tab: 0,
    books: [],
    loading: true,
    search: "",
    categoryFilter: "",
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() { this.fetchBooks(); }

  fetchBooks = async () => {
    this.setState({ loading: true });
    try {
      const { search, categoryFilter } = this.state;
      const params = { limit: 100 };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      const res = await api.get("/library/books", { params });
      const d = res.data.data || res.data;
      this.setState({ books: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.title || !form.author) { this.setState({ error: "Title and author are required." }); return; }
    this.setState({ saving: true, error: null });
    try {
      const payload = { ...form, totalCopies: parseInt(form.totalCopies, 10) || 1 };
      if (editing) await api.put(`/library/books/${editing}`, payload);
      else await api.post("/library/books", payload);
      this.setState({ open: false, saving: false });
      this.fetchBooks();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleIssue = async (bookId) => {
    const studentNo = window.prompt("Enter student number to issue this book:");
    if (!studentNo) return;
    try {
      await api.post("/library/issues", { bookId, studentNo, dueDate: "" });
      this.fetchBooks();
    } catch (e) {
      alert(e.response?.data?.message || "Failed to issue book.");
    }
  };

  openEdit = (b) => {
    this.setState({
      open: true, editing: b.id, error: null,
      form: {
        title: b.title || "",
        author: b.author || "",
        isbn: b.isbn || "",
        category: b.category || "",
        totalCopies: String(b.totalCopies || 1),
        description: b.description || ""
      }
    });
  };

  render() {
    const { classes } = this.props;
    const { books, loading, search, categoryFilter, open, editing, form, saving, error } = this.state;

    const columns = [
      { id: "title", header: "Title", accessorFn: b => b.title },
      { id: "author", header: "Author", accessorFn: b => b.author },
      { id: "isbn", header: "ISBN", accessorFn: b => b.isbn || "—", meta: { variant: "mono" } },
      { id: "category", header: "Category", accessorFn: b => b.category || "—" },
      { id: "copies", header: "Copies", accessorFn: b => b.totalCopies || 0 },
      {
        id: "availability",
        header: "Availability",
        cell: info => {
          const b = info.row.original;
          const available = (b.totalCopies || 0) - (b.issuedCopies || 0);
          return <Pill tone={available > 0 ? "success" : "danger"}>{available > 0 ? `${available} avail.` : "All issued"}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const b = info.row.original;
          const available = (b.totalCopies || 0) - (b.issuedCopies || 0);
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(b)}>Edit</Button>
              {available > 0 && (
                <Button size="sm" color="success" simple onClick={() => this.handleIssue(b.id)}>Issue</Button>
              )}
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar
            tabs={["Catalogue", "Borrowed"]}
            activeTab={this.state.tab || 0}
            onTabChange={tab => this.setState({ tab })}
            actions={[]}
          />
          <div className={classes.searchBar}>
            <input
              className={classes.searchInput}
              type="text"
              placeholder="Search by title, author, or ISBN..."
              value={search}
              onChange={e => this.setState({ search: e.target.value })}
              onKeyDown={e => e.key === "Enter" && this.fetchBooks()}
            />
            <FormControl style={{ minWidth: "160px" }}>
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} onChange={e => this.setState({ categoryFilter: e.target.value }, this.fetchBooks)}>
                <MenuItem value="">— All —</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="sm" onClick={this.fetchBooks} color="primary" style={{ marginBottom: "2px" }}>
              Search
            </Button>
          </div>

          <DataTable
            title="Library Catalogue"
            subtitle="Books, borrowing records, and availability"
            actions={
              <Button size="sm" color="primary" onClick={() => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null })}>
                + Add Book
              </Button>
            }
            columns={columns}
            data={books}
            isLoading={loading}
            emptyState="No books found."
          />
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Book" : "Add Book to Catalogue"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Title *" id="title" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.title, onChange: this.handleChange("title") }} />
            <CustomInput labelText="Author *" id="author" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.author, onChange: this.handleChange("author") }} />
            <CustomInput labelText="ISBN" id="isbn" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.isbn, onChange: this.handleChange("isbn") }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} onChange={this.handleChange("category")}>
                <MenuItem value="">— Select —</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomInput labelText="Total Copies" id="totalCopies" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.totalCopies, onChange: this.handleChange("totalCopies"), type: "number", min: "1" }} />
            <CustomInput labelText="Description" id="description" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.description, onChange: this.handleChange("description"), multiline: true, rows: 2 }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

LibraryCatalogue.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(LibraryCatalogue));
